import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Icons, iconSize, Typography, webFonts, webPalette } from '@munifocus/ui';
import { useCookieConsent } from '../providers/CookieConsentProvider';
import { CookiePreferencesModal } from './CookiePreferencesModal';

export function CookieBanner() {
  const { t } = useTranslation();
  const { hasConsented, acceptAll, rejectAll, openPreferences } = useCookieConsent();

  return (
    <>
      {!hasConsented && (
        <Box
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
          sx={{
            position: 'fixed',
            zIndex: 60,
            bottom: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 'auto' },
            width: { sm: 420 },
            maxWidth: 'calc(100vw - 32px)',
            backgroundColor: webPalette.white,
            border: `1px solid ${webPalette.line}`,
            borderRadius: '14px',
            boxShadow: `0 24px 48px -24px ${webPalette.overlayBlackShadow}`,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: webPalette.parch,
              }}
            >
              <Icons.Cookie size={iconSize.lg} color={webPalette.tealInk} aria-hidden />
            </Box>
            <Box>
              <Typography
                id="cookie-banner-title"
                component="h2"
                sx={{
                  fontFamily: webFonts.display,
                  fontWeight: 700,
                  fontSize: 17,
                  color: webPalette.ink,
                  mb: 0.5,
                }}
              >
                {t('web.cookies.banner.title')}
              </Typography>
              <Typography
                id="cookie-banner-description"
                sx={{ fontSize: 14, lineHeight: 1.55, color: webPalette.text }}
              >
                {t('web.cookies.banner.description')}{' '}
                <Box
                  component={RouterLink}
                  to="/cookie-policy"
                  sx={{
                    color: webPalette.tealInk,
                    fontWeight: 600,
                    textDecoration: 'underline',
                    '&:hover': { color: webPalette.green },
                  }}
                >
                  {t('web.cookies.banner.learnMore')}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <SolidButton onClick={acceptAll}>{t('web.cookies.actions.acceptAll')}</SolidButton>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <GhostButton onClick={rejectAll} grow>
                {t('web.cookies.actions.rejectAll')}
              </GhostButton>
              <GhostButton onClick={openPreferences} grow>
                {t('web.cookies.actions.customize')}
              </GhostButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Rendered regardless of consent so it can be re-opened from the footer. */}
      <CookiePreferencesModal />
    </>
  );
}

function SolidButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        width: '100%',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        color: webPalette.onDark,
        backgroundColor: webPalette.green,
        border: '1px solid transparent',
        borderRadius: '6px',
        px: 2,
        py: 1.25,
        transition: 'all 0.2s',
        '&:hover': { backgroundColor: webPalette.greenDeep },
      }}
    >
      {children}
    </Box>
  );
}

function GhostButton({
  onClick,
  children,
  grow,
}: {
  onClick: () => void;
  children: React.ReactNode;
  grow?: boolean;
}) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        flex: grow ? 1 : 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        color: webPalette.ink,
        backgroundColor: 'transparent',
        border: `1px solid ${webPalette.borderOnLight}`,
        borderRadius: '6px',
        px: 2,
        py: 1,
        transition: 'all 0.2s',
        '&:hover': { borderColor: webPalette.tealInk, color: webPalette.tealInk },
      }}
    >
      {children}
    </Box>
  );
}
